/**
 * VerifyEmail — the gate shown to authenticated-but-unverified email/password
 * users. Google users never see this (their email is verified by Google).
 *
 * The user clicks the link in their inbox, then "I've verified" here, which
 * reloads the Firebase user + force-refreshes the token so the backend sees
 * email_verified=true (firing the welcome email) and unlocks the app.
 */

import React, { useState } from 'react';
import { MailCheck, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';

export const VerifyEmail: React.FC = () => {
  const { user, refreshVerification, resendVerification, signOut } = useAuth();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleContinue = async () => {
    setChecking(true);
    setError(null);
    setNotice(null);
    try {
      const verified = await refreshVerification();
      if (verified) {
        // Re-bootstrap the whole app so the now-verified (and auto-approved)
        // state loads cleanly. Reloading avoids a stale in-memory auth state
        // leaving the user stuck on this screen (they previously had to sign
        // out and back in).
        window.location.reload();
        return;
      }
      setError('Not verified yet. Click the link in your email (check spam/junk too), then try again.');
    } catch {
      setError('Could not check verification. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      await resendVerification();
      setNotice('Verification email sent. Check your inbox (and spam).');
    } catch {
      setError('Could not resend right now. Please wait a moment and try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <MailCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Verify your email</CardTitle>
          <CardDescription>
            We sent a verification link to{' '}
            <span className="font-medium text-foreground">{user?.email ?? 'your email'}</span>.
            Click it, then come back here.
            <span className="mt-2 block text-xs">
              Don't see it? <span className="font-medium text-foreground">Check your spam or junk folder</span> (and allow a minute for it to arrive).
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {notice && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{notice}</AlertDescription>
            </Alert>
          )}

          <Button className="w-full" onClick={handleContinue} disabled={checking}>
            {checking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking…
              </>
            ) : (
              'I’ve verified — continue'
            )}
          </Button>

          <Button variant="outline" className="w-full" onClick={handleResend} disabled={resending}>
            {resending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending…
              </>
            ) : (
              'Resend verification email'
            )}
          </Button>

          <div className="text-center text-sm text-muted-foreground">
            Wrong account?{' '}
            <button type="button" className="font-medium text-foreground hover:underline" onClick={signOut}>
              Sign out
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
